import { useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useQueryClient } from '@tanstack/react-query'
import { Field } from '@/components/forge/Field'
import { ForgeButton, IconButton } from '@/components/forge/ForgeButton'
import { ForgeDrawer } from '@/components/forge/ForgeDrawer'
import { ForgeSelect } from '@/components/forge/ForgeSelect'
import { HierarchyPicker } from '@/components/forge/HierarchyPicker'
import { Icon } from '@/components/forge/Icon'
import { ErrorState, SkeletonRows } from '@/components/forge/States'
import { Eyebrow, Num } from '@/components/forge/primitives'
import { useAccount } from '@/features/org/account'
import { createEstate } from '@/features/hierarchy/estate'
import { fetchNode } from '@/features/hierarchy/api'
import { queryKeys } from '@/services/queryClient'
import { usePermissionCatalogue, useRoles } from '@/features/rbac/queries'
import { lookFor } from '@/features/shiftPatterns/api'
import { useShiftPatterns } from '@/features/shiftPatterns/queries'
import {
  SCOPE_TIERS,
  liveAssignments,
  reconcileGrants,
} from '@/features/users/api'
import { allowedTiers, inferPlaces, resolveRole } from '@/features/users/scope'
import { useCreateUser, useUpdateUser, useUser } from '@/features/users/queries'
import { useToast } from '@/providers/ToastProvider'
import { ON_TINT_CLASS, TINT_CLASS } from '@/lib/tone'
import { cn } from '@/lib/cn'

/**
 * Invite a user, or change one.
 *
 * ── Why this replaced a four-field dialog ────────────────────────────────
 * The demo's Create asked a name, a role, a plant and a team, and derived the
 * email address from the name. Every one of those four changes here: the API
 * takes a first and last name separately, a real address it will send mail to,
 * roles as a list of grants rather than one key, and no team at all.
 *
 * So it is a drawer and not a dialog. A dialog is for one decision; granting
 * somebody two roles at three sites each is a dozen, and it is worth being able
 * to see the directory behind it while you do.
 *
 * ── The hard part is the scope, and the API is exact about it ────────────
 * A role is a bundle of capabilities. WHERE somebody may use them is chosen when
 * they are granted it, per PERMISSION, and the server enforces four rules —
 * spelled out in `features/users/api.js`. Two of them shape this form:
 *
 *   Every permission in the role must be sent, all of them. So granting a role
 *   here means every permission it holds appears with its own scope row, and
 *   none of them can be dropped. The form does not offer a checkbox per
 *   permission, because an unchecked one is a 422 rather than a narrower grant.
 *
 *   A permission's scope must be at a level its `targetLevels` allows. Which is
 *   why the picker on each row offers different nodes: `account.manage` is
 *   account-only and shows one option, `assets.manage_assets` can be granted at
 *   any of the six and shows the estate.
 *
 * The role's own scopes are NOT asked for. They are the union of its
 * permissions' and are derived at submit — two controls for one fact is how an
 * unreadable 422 gets produced.
 *
 * ── "Same everywhere" exists because it is the common case ──────────────
 * Almost every grant is one place: this person, this role, at Findlay. Setting
 * that six times because the role happens to hold six permissions is the kind of
 * form people fill in wrongly out of fatigue. So the role card has a picker that
 * applies a node to every permission that ALLOWS it — and says how many it
 * reached, because a node valid for four of six permissions is not "everywhere"
 * and pretending otherwise would be the quiet kind of wrong.
 *
 * ── Shift assignment ────────────────────────────────────────────────────
 * This was `shifts: []` with a note saying a picker would offer fixture ids the
 * server would refuse. Settings → Shift Master is live now, so the ids are real
 * and the picker is built.
 *
 * An assignment is a shift plus the dates it applies between. `start_date` is
 * required; `end_date` is nullable and means "until further notice", which is
 * the common case — most people are on a shift open-endedly and come off it when
 * somebody decides, not on a date chosen months earlier. So the row reads as a
 * checkbox for "ongoing" rather than as an empty date field somebody has to
 * understand.
 *
 * `shifts: []` is still valid and still the default for a new user: not everyone
 * works a shift, and a plant manager on no pattern is a normal record rather
 * than an incomplete one.
 *
 * What the server refuses: a duplicate `shift_id` (merge the dates into one
 * entry — the picker cannot produce one, since an assigned shift is removed
 * from the Add list), and a shift this account does not own. It does NOT check
 * that `end_date` falls after `start_date`, so the form does.
 */

export function UserEditorDrawer({ request, onClose, onSaved }) {
  if (!request) return null
  return (
    <Editor
      key={`${request.mode}-${request.id ?? 'new'}`}
      request={request}
      onClose={onClose}
      onSaved={onSaved}
    />
  )
}

function Editor({ request, onClose, onSaved }) {
  const { t } = useTranslation(['app', 'common'])
  const { toast } = useToast()

  const editing = request.mode === 'edit'

  /* The person, read fresh when the drawer opens. The list row carries no
     grants, and the PUT replaces every one of them — so a form built from the
     row would revoke everything it could not see. */
  const person = useUser(editing ? request.id : null)

  const roles = useRoles()
  const catalogue = usePermissionCatalogue()
  /* The shift patterns this account runs — the same live set Settings → Shift
     Master edits. Read here so the form opens with its options in hand. */
  const shiftPatterns = useShiftPatterns()
  /* The account's own id, which is what an account-level scope is spelled with.
     `/auth/me` serves the organisation's NAME and not its id, so this is the
     only source — and it is the whole reason the editor touches `/account` at
     all.

     ── It can be refused, and that is not fatal ──────────────────────────
     `GET /account` requires `account.manage`; this drawer is gated on
     `users.manage`. Somebody holding the second and not the first is an ordinary
     user administrator, and they get a 403 here. The form still opens — every
     grant below the account level works without it — and the picker says, in
     words, that the account level is missing and why. It used to drop the option
     silently, which reads as "this account has no top level". */
  const account = useAccount()

  const create = useCreateUser()
  const update = useUpdateUser()
  const save = editing ? update : create

  /* `account.isPending` is awaited; `account.isError` is not fatal — see above.
     The hierarchy is no longer read here at all: it is drilled a generation at a
     time from inside the picker, and walked on demand by the resolver. */
  const loading =
    (editing && person.isPending) ||
    roles.isPending ||
    catalogue.isPending ||
    shiftPatterns.isPending ||
    account.isPending

  const failed = editing ? person.isError : false

  return (
    <ForgeDrawer
      open
      onOpenChange={(next) => {
        if (!next) onClose()
      }}
      title={editing ? t('settings.users.editTitle') : t('settings.users.inviteTitle')}
      closeLabel={t('common:actions.close')}
      className="w-[min(42rem,100vw)]"
      footer={
        <>
          <ForgeButton onClick={onClose}>{t('common:actions.cancel')}</ForgeButton>
          <ForgeButton
            type="submit"
            form="user-editor"
            variant="primary"
            disabled={loading || save.isPending}
          >
            {save.isPending
              ? t('settings.users.saving')
              : editing
                ? t('common:actions.save')
                : t('settings.users.invite')}
          </ForgeButton>
        </>
      }
    >
      {failed ? (
        <ErrorState
          error={person.error}
          title={t('settings.users.loadOneFailed')}
          onRetry={person.refetch}
        />
      ) : loading ? (
        <SkeletonRows count={6} />
      ) : (
        <Form
          editing={editing}
          person={person.data ?? null}
          roles={roles.data?.items ?? []}
          modules={catalogue.data?.modules ?? []}
          shifts={shiftPatterns.data?.items ?? []}
          account={account.data ?? null}
          save={save}
          onSaved={(saved) => {
            toast(
              t(editing ? 'settings.users.updated' : 'settings.users.invited', {
                name: saved.name,
              }),
              'ok',
            )
            onSaved(saved)
          }}
        />
      )}
    </ForgeDrawer>
  )
}

function Form({ editing, person, roles, modules, shifts, account, save, onSaved }) {
  const { t } = useTranslation(['app', 'common'])
  const queryClient = useQueryClient()

  const [firstName, setFirstName] = useState(person?.firstName ?? '')
  const [lastName, setLastName] = useState(person?.lastName ?? '')
  const [email, setEmail] = useState(person?.email ?? '')
  const [isActive, setIsActive] = useState(person?.isActive ?? true)

  /**
   * The grants being edited: `roleId → (permissionId → Map(scopeKey → scope))`.
   *
   * A Map of Maps rather than nested arrays because every operation here is a
   * membership test — is this node already a scope on this permission — and an
   * array would make each of them a scan. It is also what stops the same node
   * being added twice, which the server rejects as a duplicate.
   */
  const roleById = useMemo(() => new Map(roles.map((role) => [role.id, role])), [roles])

  const permissionById = useMemo(() => {
    const out = new Map()
    for (const module of modules) {
      for (const permission of module.permissions) out.set(permission.id, { ...permission, module })
    }
    return out
  }, [modules])

  /**
   * The estate, walked on demand.
   *
   * This used to be `indexNodes(...)` over the flat list `GET /assets/hierarchy`
   * returned — the whole account in memory, every lookup instant. That endpoint
   * is going away, so the ancestors and descendants the scope rules need are now
   * fetched a generation at a time. Same rules, same answers; what changed is
   * that every one of them is a promise, which is why the card below has a
   * pending state it did not used to need.
   *
   * The account's real id is applied HERE, at the one seam, to the root node and
   * to its children's `parentId` together. The previous version swapped only the
   * root's own id, which left every region pointing at a sentinel parent that no
   * longer existed — so no walk ever crossed the account, and `account.manage`
   * granted at a site resolved to "cannot be granted" instead of widening.
   */
  const estate = useMemo(
    () => createEstate({ queryClient, account: account ? { id: account.id, name: account.name } : null }),
    [queryClient, account],
  )

  /* The row the picker opens on, and the one place an account-level scope can be
     chosen. `null` when `/account` was refused — the picker then starts at the
     region list and says why the level above it is missing. */
  const root = estate.root
  const accountUnavailable = !root

  const [granted, setGranted] = useState(() => initialGrants(person, roleById))


  /**
   * Where each granted role applies: `roleId → [hierarchy node]`.
   *
   * The control people actually use. `granted` stays the source of truth for
   * what gets SENT — it is per permission, because the API is — but for a role
   * in the simple view it is derived from this and never edited directly.
   *
   * Seeded by asking whether each stored grant can be described this way at all
   * (`inferPlaces`). One written by a form that scoped every permission
   * separately cannot, and opens in the detailed view rather than being
   * silently flattened into something simpler than what somebody built.
   */
  const [places, setPlaces] = useState(() => new Map())
  /* There is no separate "detailed" flag: a role ABSENT from `places` is one
     being scoped per permission. One source for one fact — a boolean beside it
     is the second place for the two to disagree. */

  /**
   * What resolving those places meant: `roleId → {broadened, expanded, impossible}`.
   *
   * State rather than a `useMemo`, because the resolution is now a walk over the
   * API and not a pure function of what is on screen. The card reads this; it
   * cannot derive it.
   */
  const [outcomes, setOutcomes] = useState(() => new Map())
  /** Roles whose resolution is in flight. The card says so rather than showing stale counts. */
  const [resolving, setResolving] = useState(() => new Set())
  /** The walk failed. Said once, at the top of the form, because it blocks saving. */
  const [resolveFailed, setResolveFailed] = useState(false)

  /**
   * Which stored grants can be shown as "the role, at these places" — the
   * `inferPlaces` pass, which is now a walk and so runs after the first paint.
   *
   * Roles stay in the detailed view until it answers, so the editor never shows
   * the simple control seeded with a half-finished guess. `seeding` holds the
   * grants section on a skeleton while it runs; nothing else on the form waits,
   * because nothing else depends on it.
   */
  const [seeding, setSeeding] = useState(() => editing && granted.size > 0)

  /**
   * `nodeId → node`, for scopes that arrived as bare ids.
   *
   * A stored grant names its scopes as `{tier, id}` and nothing else. The flat
   * node list used to supply the name; with it gone, a chip in the detailed view
   * would otherwise read as a UUID. Nodes chosen through the picker are
   * remembered on the way past (`rememberNodes`), and the rest are read one
   * record at a time by the effect below — only for roles actually showing their
   * scopes, so a grant that collapses to the simple view costs nothing.
   */
  const [nodeNames, setNodeNames] = useState(() => new Map())

  /**
   * `plantId → node` for the plants that own a granted role.
   *
   * A role created at a plant (`role.plantId`) can only be granted at that plant
   * or beneath it — the server refuses anything else with a bare `scopes` 400
   * that names no row (`_validate_roles_payload`, "A plant's role can only be
   * assigned inside that plant"). So the card's pickers open AT the plant rather
   * than at the account, which keeps the wrong places out of reach, and the node
   * is what they need as their root. Read once per plant, through the same cache
   * the name resolver uses.
   */
  const [rolePlants, setRolePlants] = useState(() => new Map())

  const missingPlants = useMemo(() => {
    const out = new Set()
    for (const roleId of granted.keys()) {
      const plantId = roleById.get(roleId)?.plantId
      if (plantId && !rolePlants.has(plantId)) out.add(plantId)
    }
    return out
  }, [granted, roleById, rolePlants])

  useEffect(() => {
    if (!missingPlants.size) return undefined
    let cancelled = false

    ;(async () => {
      const found = await Promise.all(
        [...missingPlants].map(async (id) => {
          try {
            return await queryClient.fetchQuery({
              queryKey: queryKeys.hierarchyNode('site', id),
              queryFn: ({ signal }) => fetchNode({ tier: 'site', id }, signal),
              staleTime: 60_000,
            })
          } catch {
            /* Unreadable plant. The picker still roots at its id — the rule is
               about the id, not the name — and the label falls back to it. */
            return { id, tier: 'site', parentId: root?.id ?? null, name: id, code: null }
          }
        }),
      )
      if (cancelled) return
      setRolePlants((current) => {
        const next = new Map(current)
        for (const node of found) next.set(node.id, node)
        return next
      })
    })()

    return () => {
      cancelled = true
    }
  }, [missingPlants, queryClient, root])

  /* `unresolved` nodes are the id standing in for a name while the record is
     still being read. Letting one of those into the cache would pin the chip to
     its own id permanently, because the resolver below only looks for ids it has
     no entry for. */
  const rememberNodes = (list) =>
    setNodeNames((current) => {
      const next = new Map(current)
      for (const node of list) if (!node.unresolved) next.set(node.id, node)
      return next
    })

  /* Which ids are on screen as raw scopes right now: the detailed view's, and
     only the detailed view's. */
  const unnamed = useMemo(() => {
    const out = new Map()
    /* Not while `inferPlaces` is still running. Until it answers, `places` is
       empty and EVERY role looks like one showing its scopes — so this would read
       back a record per scope on the whole grant, moments before most of them
       collapse into a single chip and stop being shown at all. */
    if (seeding) return out
    for (const [roleId, permissions] of granted) {
      if (places.has(roleId)) continue
      for (const scopes of permissions.values()) {
        for (const scope of scopes.values()) {
          if (!nodeNames.has(scope.id)) out.set(scope.id, scope)
        }
      }
    }
    return out
  }, [granted, places, nodeNames, seeding])

  useEffect(() => {
    if (!unnamed.size) return undefined
    let cancelled = false

    ;(async () => {
      /* In batches, not all at once. A grant that was expanded across every
         plant in a large account is a lot of ids, and firing them together is
         how a form opening turns into a few hundred simultaneous requests. */
      const pending = [...unnamed.values()]
      const found = []
      for (let at = 0; at < pending.length; at += NAME_BATCH) {
        const batch = await Promise.all(
          pending.slice(at, at + NAME_BATCH).map(async (scope) => {
            try {
              return await queryClient.fetchQuery({
                queryKey: queryKeys.hierarchyNode(scope.tier, scope.id),
                queryFn: ({ signal }) => fetchNode({ tier: scope.tier, id: scope.id }, signal),
                staleTime: 60_000,
              })
            } catch {
              /* A scope naming a node that has since been deleted. The chip
                 falls back to the id, which is the honest thing to show and is
                 still enough to remove it. */
              return null
            }
          }),
        )
        if (cancelled) return
        found.push(...batch.filter(Boolean))
      }
      if (!cancelled && found.length) rememberNodes(found)
    })()

    return () => {
      cancelled = true
    }
  }, [unnamed, queryClient])

  useEffect(() => {
    if (!seeding) return undefined
    let cancelled = false

    ;(async () => {
      const nextPlaces = new Map()
      const nextOutcomes = new Map()
      let failed = false

      for (const [roleId, permissionScopes] of granted) {
        const role = roleById.get(roleId)
        if (!role) continue

        const permissions = role.permissionIds.map((id) => permissionById.get(id)).filter(Boolean)
        /* A role the catalogue cannot fully describe cannot be reasoned about;
           the detailed view at least renders the ids. */
        if (permissions.length !== role.permissionIds.length) continue

        try {
          const inferred = await inferPlaces(permissionScopes, permissions, estate)
          if (inferred) {
            nextPlaces.set(roleId, inferred.places)
            nextOutcomes.set(roleId, await resolveRole(inferred.places, permissions, estate))
          }
        } catch {
          /* This role simply opens in the detailed view, which is the honest
             fallback and the same one an unresolvable grant already took. It is
             not the whole-form failure that `resolveFailed` is for — that is
             reserved for a walk the person asked for by choosing a place. */
          failed = true
        }
      }

      if (cancelled) return
      setPlaces(nextPlaces)
      setOutcomes(nextOutcomes)
      if (failed) setResolveFailed(true)
      setSeeding(false)
    })()

    return () => {
      cancelled = true
    }
    // Once, on open. `granted` is edited below and must not re-trigger this.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  /**
   * Shift assignments being edited: `[{ shiftId, startDate, endDate }]`.
   *
   * A plain array, not a Map — unlike the grants, nothing here is a membership
   * test on a nested structure, and the order somebody added them in is the
   * order worth showing back.
   *
   * Seeded from what the server holds, minus any assignment whose pattern has
   * been deleted — `liveAssignments`, shared with the row menu's Deactivate so
   * there is one rule rather than two. Without it, deleting a pattern makes
   * every person on it unsaveable.
   */
  const [assigned, setAssigned] = useState(() =>
    liveAssignments(person?.shifts, new Set(shifts.map((shift) => shift.id))),
  )


  /** Roles not yet granted, which is what the Add control may offer. */
  const available = roles.filter((role) => !granted.has(role.id))

  /* Field-keyed refusals. The server's own wording is never rendered — it is
     untranslated and can name internals — so the key is read as a signal and
     this screen says it in the product's language. */
  const emailTaken = Boolean(save.error?.fields?.email)
  const shiftRejected = Boolean(save.error?.fields?.shifts)
  const scopeRejected = Boolean(
    save.error?.fields?.scopes ||
      save.error?.fields?.permissions ||
      save.error?.fields?.roles ||
      SCOPE_TIERS.some((entry) => save.error?.fields?.[entry.field]),
  )

  function grantRole(roleId) {
    const role = roleById.get(roleId)
    if (!role) return
    setGranted((current) => {
      const next = new Map(current)
      /* Every permission the role holds, each with an empty scope set. All of
         them, because a subset is a 422 — see the header. They fill in as soon
         as a place is chosen. */
      next.set(
        roleId,
        new Map(role.permissionIds.map((permissionId) => [permissionId, new Map()])),
      )
      return next
    })
    setPlaces((current) => new Map(current).set(roleId, []))
  }

  /**
   * The resolution each role is currently waiting on.
   *
   * Choosing a place is now a walk over the API, so two quick changes can land
   * out of order — pick Findlay, pick Lima, and Findlay's slower answer
   * overwrites Lima's. Each run is stamped and only the newest one for a role is
   * allowed to write. A ref rather than state: it is bookkeeping between renders,
   * and rendering it would be the second place for the two to disagree.
   */
  const resolveRun = useRef(new Map())

  /**
   * Choose where a role applies, and derive every permission's scopes from it.
   *
   * The one write the simple view makes. `resolveRole` does the thinking — see
   * `features/users/scope.js` — and this stores both halves: the places, which
   * is what the control shows, and the resolved scopes, which is what is sent.
   *
   * The places land immediately and the scopes land when the walk returns, so
   * the chip appears the moment it is chosen and the card reports what it meant a
   * moment later. `unscoped` covers the gap: until the scopes arrive the save is
   * blocked anyway, for exactly the right reason.
   */
  async function setRolePlaces(roleId, nextPlaces) {
    const role = roleById.get(roleId)
    if (!role) return

    const permissions = role.permissionIds
      .map((id) => permissionById.get(id))
      .filter(Boolean)

    const run = (resolveRun.current.get(roleId) ?? 0) + 1
    resolveRun.current.set(roleId, run)

    setPlaces((current) => new Map(current).set(roleId, nextPlaces))
    setResolving((current) => new Set(current).add(roleId))

    try {
      const result = await resolveRole(nextPlaces, permissions, estate)
      if (resolveRun.current.get(roleId) !== run) return

      setOutcomes((current) => new Map(current).set(roleId, result))
      setGranted((current) => {
        const next = new Map(current)
        next.set(
          roleId,
          new Map(
            role.permissionIds.map((id) => [id, result.permissionScopes.get(id) ?? new Map()]),
          ),
        )
        return next
      })
    } catch {
      if (resolveRun.current.get(roleId) === run) setResolveFailed(true)
    } finally {
      if (resolveRun.current.get(roleId) === run) {
        setResolving((current) => {
          const next = new Set(current)
          next.delete(roleId)
          return next
        })
      }
    }
  }

  function revokeRole(roleId) {
    /* Bump the run so an in-flight resolution for this role cannot write back
       scopes for a grant that no longer exists. */
    resolveRun.current.set(roleId, (resolveRun.current.get(roleId) ?? 0) + 1)
    setGranted((current) => {
      const next = new Map(current)
      next.delete(roleId)
      return next
    })
    setPlaces((current) => {
      const next = new Map(current)
      next.delete(roleId)
      return next
    })
    setOutcomes((current) => {
      const next = new Map(current)
      next.delete(roleId)
      return next
    })
  }

  /**
   * Leave the simple view for this role and edit each permission directly.
   *
   * One-way on purpose. Coming back would mean re-deriving places from whatever
   * was hand-built, and `inferPlaces` says that is usually impossible — so the
   * honest offer is "start again", which revoking and re-granting the role
   * already is.
   */
  function openDetail(roleId) {
    resolveRun.current.set(roleId, (resolveRun.current.get(roleId) ?? 0) + 1)
    setPlaces((current) => {
      const next = new Map(current)
      next.delete(roleId)
      return next
    })
    setOutcomes((current) => {
      const next = new Map(current)
      next.delete(roleId)
      return next
    })
  }

  /**
   * The detailed view's write: one permission's scopes, set wholesale.
   *
   * It takes the chosen NODES rather than an id to toggle, because the picker is
   * now a modal that returns a selection — and because there is no flat node list
   * left to look an id up in. The node's name is remembered in `nodeNames` on the
   * way through, which is what lets the chip read as a place rather than as a
   * UUID after the modal closes.
   */
  function setPermissionScopes(roleId, permissionId, chosen) {
    rememberNodes(chosen)
    setGranted((current) => {
      const next = new Map(current)
      const permissions = new Map(next.get(roleId))
      permissions.set(
        permissionId,
        new Map(chosen.map((node) => [node.id, { tier: node.tier, id: node.id }])),
      )
      next.set(roleId, permissions)
      return next
    })
  }

  const shiftById = useMemo(() => new Map(shifts.map((shift) => [shift.id, shift])), [shifts])

  /** Patterns not yet assigned. An assigned one cannot be picked twice. */
  const availableShifts = shifts.filter(
    (shift) => !assigned.some((entry) => entry.shiftId === shift.id),
  )

  /**
   * Assignments the server would take but that describe nothing.
   *
   * `end_date` before `start_date` is a window that never opens. The API does
   * not check it — `_validate_shifts_payload` verifies ids and duplicates only —
   * so this is the only thing standing between somebody and a stored assignment
   * that can never match a day.
   */
  const badDates = useMemo(
    () => assigned.filter((entry) => entry.endDate && entry.endDate < entry.startDate),
    [assigned],
  )

  /**
   * Permissions with no scope at all. The server refuses each one.
   *
   * Still counted over `granted`, which is the payload, and not over the places
   * — a role in the simple view with no place chosen produces exactly this, and
   * so does one whose permissions cannot attach anywhere the chosen places
   * allow. The card explains which of the two it is; this only has to stop the
   * save.
   */
  const unscoped = useMemo(() => {
    const out = []
    for (const [roleId, permissions] of granted) {
      for (const [permissionId, scopes] of permissions) {
        if (scopes.size === 0) out.push({ roleId, permissionId })
      }
    }
    return out
  }, [granted])

  /**
   * Scopes on a plant's role that fall outside that plant: `roleId → [scope]`.
   *
   * The pickers are rooted at the plant, so a new choice cannot land here. What
   * can is the simple view's "the whole account" chosen before this existed, or a
   * stored grant carried in from elsewhere — and the server refuses the whole
   * save for either. Counted over `granted`, the payload, like `unscoped`.
   */
  const outside = useMemo(() => {
    const out = new Map()
    for (const [roleId, permissions] of granted) {
      const plantId = roleById.get(roleId)?.plantId
      if (!plantId) continue
      const found = new Map()
      for (const scopes of permissions.values()) {
        for (const scope of scopes.values()) {
          if (outsidePlant(scope, plantId)) found.set(scope.id, scope)
        }
      }
      if (found.size) out.set(roleId, [...found.values()])
    }
    return out
  }, [granted, roleById])

  function submit(event) {
    event.preventDefault()
    if (unscoped.length || badDates.length || outside.size) return

    const payload = {
      firstName,
      lastName,
      roles: [...granted].map(([roleId, permissions]) => ({
        roleId,
        permissions: [...permissions].map(([permissionId, scopes]) => ({
          permissionId,
          scopes: [...scopes.values()],
        })),
      })),
      /* The whole set, every time. `shifts` is full-replace like `roles`: an
         assignment left out of the payload is revoked, so what the form shows
         is what the person will have. */
      shifts: assigned,
    }

    const mutation = editing
      ? { ...payload, id: person.id, isActive }
      : { ...payload, email }

    save.mutate(mutation, { onSuccess: onSaved })
  }

  return (
    <form id="user-editor" onSubmit={submit} className="flex flex-col gap-5">
      <div className="grid grid-cols-2 gap-3 max-sm:grid-cols-1">
        <Field
          label={t('settings.users.fields.firstName')}
          required
          value={firstName}
          maxLength={100}
          onChange={(event) => setFirstName(event.target.value)}
        />
        <Field
          label={t('settings.users.fields.lastName')}
          required
          value={lastName}
          maxLength={100}
          onChange={(event) => setLastName(event.target.value)}
        />
      </div>

      {editing ? (
        /* No email field on an edit. `UserUpdate` has none: a login address is
           changed by its owner through their own profile, which mails a
           confirmation link to the new address and switches it only once that is
           clicked. An admin reassigning somebody's login from here would be a
           takeover with no notification attached. */
        <div className="flex flex-col gap-1.5">
          <Eyebrow>{t('settings.users.fields.email')}</Eyebrow>
          <p className="m-0 text-body-sm text-ink">{person.email}</p>
          <p className="m-0 text-micro text-ink-mute">{t('settings.users.emailFixed')}</p>
        </div>
      ) : (
        <Field
          type="email"
          label={t('settings.users.fields.email')}
          hint={t('settings.users.emailHint')}
          required
          value={email}
          maxLength={255}
          error={emailTaken ? t('settings.users.emailTaken') : undefined}
          onChange={(event) => setEmail(event.target.value)}
        />
      )}

      {editing ? (
        <label className="flex items-start gap-2.5">
          <input
            type="checkbox"
            className="mt-0.5 accent-forge"
            checked={isActive}
            onChange={(event) => setIsActive(event.target.checked)}
          />
          <span className="flex flex-col">
            <span className="text-control text-ink">{t('settings.users.fields.isActive')}</span>
            <span className="text-micro text-ink-mute">{t('settings.users.isActiveHint')}</span>
          </span>
        </label>
      ) : null}

      <section className="flex flex-col gap-2.5 border-t border-subtle pt-4">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <Eyebrow>{t('settings.users.access')}</Eyebrow>
          {available.length ? (
            <ForgeSelect
              id="user-add-role"
              labelHidden
              size="sm"
              label={t('settings.users.addRole')}
              value=""
              placeholder={t('settings.users.addRole')}
              onValueChange={grantRole}
              className="w-[15rem]"
              options={available.map((role) => ({ value: role.id, label: role.name }))}
            />
          ) : null}
        </div>

        {/* Zero roles is allowed and is not a mistake: the account is created,
            the invitation is sent, and access is a second decision that does not
            have to be taken in the same minute. The sentence says what will
            happen rather than letting somebody find out. */}
        {granted.size === 0 ? (
          <p className="m-0 rounded-sm border border-hairline bg-sunken px-3.5 py-3 text-body-sm text-ink-dim">
            {roles.length
              ? t('settings.users.noRolesYet')
              : t('settings.users.noRolesExist')}
          </p>
        ) : seeding ? (
          /* The `inferPlaces` pass is a walk now, so the grants open on a
             skeleton rather than flickering from the detailed view into the
             simple one as each role resolves. */
          <SkeletonRows count={3} />
        ) : (
          <div className="flex flex-col gap-3">
            {[...granted].map(([roleId, permissions]) => (
              <RoleCard
                key={roleId}
                role={roleById.get(roleId)}
                plant={rolePlants.get(roleById.get(roleId)?.plantId) ?? null}
                outsideCount={outside.get(roleId)?.length ?? 0}
                permissions={permissions}
                permissionById={permissionById}
                root={root}
                accountUnavailable={accountUnavailable}
                nodeNames={nodeNames}
                places={places.get(roleId) ?? null}
                outcome={outcomes.get(roleId) ?? null}
                resolving={resolving.has(roleId)}
                onSetPlaces={(next) => setRolePlaces(roleId, next)}
                onOpenDetail={() => openDetail(roleId)}
                onRevoke={() => revokeRole(roleId)}
                onSetScopes={(permissionId, chosen) =>
                  setPermissionScopes(roleId, permissionId, chosen)
                }
              />
            ))}
          </div>
        )}

        {outside.size ? (
          <p className="m-0 text-body-sm text-on-red">{t('settings.users.outsidePlantBlocked')}</p>
        ) : null}

        {/* The per-card notices say which role and why; this is the one line
            that explains why the button will not do anything. */}
        {unscoped.length ? (
          <p className="m-0 text-body-sm text-on-amber">
            {t('settings.users.grantIncomplete')}
          </p>
        ) : null}

        {scopeRejected ? (
          <p className="m-0 text-body-sm text-on-red">{t('settings.users.grantRejected')}</p>
        ) : null}

        {/* The hierarchy walk itself failed. Distinct from a refused grant: the
            places on screen may be fine and simply could not be resolved into
            scopes, so the remedy is to try again rather than to change anything.
            Saving is already blocked, because a permission with no scopes is. */}
        {resolveFailed ? (
          <p className="m-0 text-body-sm text-on-red">{t('settings.users.resolveFailed')}</p>
        ) : null}
      </section>

      <section className="flex flex-col gap-2.5 border-t border-subtle pt-4">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <Eyebrow>{t('settings.users.shifts')}</Eyebrow>
          {availableShifts.length ? (
            <ForgeSelect
              id="user-add-shift"
              labelHidden
              size="sm"
              label={t('settings.users.addShift')}
              value=""
              placeholder={t('settings.users.addShift')}
              onValueChange={(shiftId) =>
                setAssigned((current) => [
                  ...current,
                  /* Today, because an assignment made now almost always starts
                     now, and a date field opening empty is one more thing to
                     fill in for the common case. Editable on the row. */
                  { shiftId, startDate: today(), endDate: null },
                ])
              }
              className="w-[15rem]"
              options={availableShifts.map((shift) => ({
                value: shift.id,
                label: `${shift.name} · ${shift.start}–${shift.end}`,
              }))}
            />
          ) : null}
        </div>

        {/* No shift is a normal record, not an incomplete one — a plant manager
            is on no pattern. So this states the consequence rather than asking
            for something. */}
        {assigned.length === 0 ? (
          <p className="m-0 rounded-sm border border-hairline bg-sunken px-3.5 py-3 text-body-sm text-ink-dim">
            {shifts.length
              ? t('settings.users.noShiftsYet')
              : t('settings.users.noShiftsExist')}
          </p>
        ) : (
          <ul className="m-0 flex list-none flex-col gap-2 p-0">
            {assigned.map((entry) => {
              const shift = shiftById.get(entry.shiftId)
              const look = lookFor(shift?.type)
              const invalid = Boolean(entry.endDate && entry.endDate < entry.startDate)

              return (
                <li
                  key={entry.shiftId}
                  className="rounded-sm border border-hairline bg-card px-3.5 py-2.5"
                >
                  <div className="flex flex-wrap items-center gap-2">
                    <span
                      className={cn(
                        'inline-flex items-center gap-1.5 rounded-pill px-2.5 py-0.5 text-micro font-semibold',
                        TINT_CLASS[look.tone],
                        ON_TINT_CLASS[look.tone],
                      )}
                    >
                      <Icon name={look.icon} size="xs" />
                      {shift?.name ?? entry.shiftId}
                    </span>
                    <Num className="text-micro text-ink-dim">
                      {shift ? `${shift.start}–${shift.end}` : ''}
                    </Num>
                    <span className="flex-1" />
                    <IconButton
                      bare
                      label={t('settings.users.unassignShift', { name: shift?.name ?? '' })}
                      onClick={() =>
                        setAssigned((current) =>
                          current.filter((row) => row.shiftId !== entry.shiftId),
                        )
                      }
                    >
                      <Icon name="x" size="sm" />
                    </IconButton>
                  </div>

                  <div className="mt-2 flex flex-wrap items-end gap-3">
                    <Field
                      type="date"
                      label={t('settings.users.fields.startDate')}
                      required
                      value={entry.startDate}
                      className="w-[10rem]"
                      onChange={(event) =>
                        setAssigned((current) =>
                          current.map((row) =>
                            row.shiftId === entry.shiftId
                              ? { ...row, startDate: event.target.value }
                              : row,
                          ),
                        )
                      }
                    />

                    {/* Ongoing is the common case, so it is a checkbox and the
                        date only appears when somebody says it ends. An empty
                        date field would make the normal answer look unfinished —
                        and `end_date: null` is a real value the API wants, not
                        an omission. */}
                    <label className="flex items-center gap-2 pb-2.5 text-body-sm">
                      <input
                        type="checkbox"
                        className="size-4 accent-[var(--hue-forge)]"
                        checked={entry.endDate === null}
                        onChange={(event) =>
                          setAssigned((current) =>
                            current.map((row) =>
                              row.shiftId === entry.shiftId
                                ? {
                                    ...row,
                                    endDate: event.target.checked ? null : row.startDate,
                                  }
                                : row,
                            ),
                          )
                        }
                      />
                      {t('settings.users.ongoing')}
                    </label>

                    {entry.endDate === null ? null : (
                      <Field
                        type="date"
                        label={t('settings.users.fields.endDate')}
                        value={entry.endDate}
                        min={entry.startDate}
                        className="w-[10rem]"
                        error={invalid ? t('settings.users.endBeforeStart') : undefined}
                        onChange={(event) =>
                          setAssigned((current) =>
                            current.map((row) =>
                              row.shiftId === entry.shiftId
                                ? { ...row, endDate: event.target.value }
                                : row,
                            ),
                          )
                        }
                      />
                    )}
                  </div>
                </li>
              )
            })}
          </ul>
        )}

        {shiftRejected ? (
          <p className="m-0 text-body-sm text-on-red">{t('settings.users.shiftRejected')}</p>
        ) : null}
      </section>
    </form>
  )
}

/**
 * One card per granted role.
 *
 * ── Two views, and the simple one is the default ────────────────────────
 * The question somebody is answering is "this role, where?", and that is the
 * whole of the simple view: a place picker and a sentence saying what it did.
 * Seventeen scope pickers were the API's shape showing through the screen —
 * correct, and unusable.
 *
 * The detailed view is still there, because a grant genuinely can differ per
 * permission and somebody who needs that should not be told to use a different
 * product. It opens on request, and automatically for a stored grant the simple
 * control cannot describe.
 *
 * ── The card says what the resolution did ───────────────────────────────
 * Silent widening is the failure this has to avoid: granting "Findlay" and
 * quietly handing somebody account-wide `account.manage` is the kind of thing
 * that is only discovered in an audit. So `broadened` is rendered as a sentence
 * per permission, in amber, naming the permission and where it actually landed.
 *
 * `expanded` — "the whole account" becoming every plant — is the opposite case
 * and gets a count, not a warning: it grants exactly what was asked for.
 */
function RoleCard({
  role,
  plant,
  outsideCount,
  permissions,
  permissionById,
  root,
  accountUnavailable,
  nodeNames,
  places,
  outcome,
  resolving,
  onSetPlaces,
  onOpenDetail,
  onRevoke,
  onSetScopes,
}) {
  const { t } = useTranslation(['app', 'common'])

  const rolePermissions = useMemo(
    () => [...permissions.keys()].map((id) => permissionById.get(id)).filter(Boolean),
    [permissions, permissionById],
  )

  const detailed = places === null

  /* A plant's role: the pickers start at the plant, cannot climb above it, and
     wait for its node before they open. An account-wide role keeps the account. */
  const plantId = role?.plantId ?? null
  const scopeRoot = plantId ? plant : root
  const scopeAccountUnavailable = plantId ? false : accountUnavailable
  const plantName = plant ? nodeLabel(plant) : ''
  const isOutside = plantId ? (node) => outsidePlant(node, plantId) : null

  return (
    <div className="rounded-sm border border-hairline bg-card">
      <div className="flex flex-wrap items-center justify-between gap-2 border-b border-subtle px-3.5 py-2.5">
        <div className="min-w-0">
          <p className="m-0 text-body-sm font-semibold text-ink">{role?.name ?? '—'}</p>
          <p className="m-0 text-micro text-ink-mute">
            {role?.description
              ? `${role.description} · `
              : ''}
            {t('settings.users.permissionCount', { count: rolePermissions.length })}
          </p>
          {/* Said up front, before a place is chosen — the rule is the role's,
              not the form's, and the picker opening at the plant is otherwise
              unexplained. */}
          {plantId ? (
            <p className="m-0 text-micro text-ink-mute">
              {t('settings.users.plantRole', { plant: plantName || '…' })}
            </p>
          ) : null}
        </div>
        <IconButton
          bare
          label={t('settings.users.revokeRole', { name: role?.name ?? '' })}
          onClick={onRevoke}
        >
          <Icon name="x" size="sm" />
        </IconButton>
      </div>

      {/* Which places break the rule, and what to do. The chips themselves are
          marked too; this is the sentence that says why. */}
      {outsideCount ? (
        <p className="m-0 border-b border-subtle px-3.5 py-2.5 text-micro text-on-red">
          {t('settings.users.outsidePlant', { count: outsideCount, plant: plantName || '…' })}
        </p>
      ) : null}

      {detailed ? (
        <DetailedScopes
          permissions={permissions}
          permissionById={permissionById}
          root={scopeRoot}
          accountUnavailable={scopeAccountUnavailable}
          pending={Boolean(plantId && !plant)}
          isOutside={isOutside}
          note={plantId ? t('settings.users.plantRole', { plant: plantName }) : null}
          nodeNames={nodeNames}
          onSetScopes={onSetScopes}
        />
      ) : (
        <div className="px-3.5 py-3">
          <Eyebrow as="p" className="mb-2">
            {t('settings.users.whereApplies')}
          </Eyebrow>

          <PlacePicker
            title={t('settings.users.choosePlacesFor', { role: role?.name ?? '' })}
            places={places}
            root={scopeRoot}
            accountUnavailable={scopeAccountUnavailable}
            pending={Boolean(plantId && !plant)}
            isOutside={isOutside}
            note={plantId ? t('settings.users.plantRole', { plant: plantName }) : null}
            onChange={onSetPlaces}
          />

          {places.length === 0 ? (
            <p className="mt-2 mb-0 text-micro text-on-amber">
              {t('settings.users.placeRequired')}
            </p>
          ) : null}

          {/* The walk is in flight. Said rather than left as stale counts from
              the previous selection, which would describe a grant that is no
              longer the one on screen. */}
          {resolving ? (
            <p className="mt-2 mb-0 text-micro text-ink-mute">{t('settings.users.resolving')}</p>
          ) : null}

          {/* Widening, named. The one thing that must never be silent. */}
          {!resolving && outcome?.broadened.length ? (
            <ul className="mt-2.5 mb-0 flex list-none flex-col gap-1 p-0">
              {outcome.broadened.map(({ permission, at }) => (
                <li key={permission.id} className="text-micro text-on-amber">
                  {t('settings.users.broadened', {
                    permission: permission.name,
                    place: at.tier === 'enterprise' ? t('settings.users.wholeAccount') : at.name,
                  })}
                </li>
              ))}
            </ul>
          ) : null}

          {/* Spelling out, counted. Grants no more than was asked for. */}
          {!resolving && outcome?.expanded.length ? (
            <p className="mt-2 mb-0 text-micro text-ink-mute">
              {t('settings.users.expanded', {
                count: outcome.expanded.length,
                places: outcome.expanded[0].count,
              })}
            </p>
          ) : null}

          {/* Nowhere to attach at all — a plant-level permission against an
              empty region, or against a machine. The save would 422, so it is
              said here instead. */}
          {!resolving && outcome?.impossible.length ? (
            <ul className="mt-2 mb-0 flex list-none flex-col gap-1 p-0">
              {outcome.impossible.map(({ permission }) => (
                <li key={permission.id} className="text-micro text-on-red">
                  {t('settings.users.cannotApply', { permission: permission.name })}
                </li>
              ))}
            </ul>
          ) : null}

          <button
            type="button"
            onClick={onOpenDetail}
            className="mt-2.5 text-micro text-on-forge underline decoration-from-font"
          >
            {t('settings.users.setIndividually')}
          </button>
        </div>
      )}
    </div>
  )
}

/**
 * Chosen places as chips, plus one control to open the picker.
 *
 * ── Why this is no longer a select ───────────────────────────────────────
 * It used to be the whole estate, flattened and indented by depth, in a
 * `ForgeSelect`. That reads well at demo scale and not at all beyond it: a
 * listbox cannot be searched, cannot be paged, and says nothing about where you
 * are in it. On an account with a few hundred assets it is a wall.
 *
 * So the control is a button, and the choosing happens in `HierarchyPicker` —
 * one generation at a time, searched and paged by the server, against the same
 * endpoints Settings → Sites & Hierarchy drills. The chips stay: they are the
 * answer, and the answer still belongs on the card rather than behind a dialog.
 */
function PlacePicker({
  title,
  places,
  root,
  accountUnavailable,
  pending = false,
  isOutside = null,
  note = null,
  onChange,
}) {
  const { t } = useTranslation(['app', 'common'])
  const [picking, setPicking] = useState(false)

  return (
    <div className="flex flex-wrap items-center gap-1.5">
      {places.map((place) => (
        <button
          key={place.id}
          type="button"
          onClick={() => onChange(places.filter((entry) => entry.id !== place.id))}
          className={chipClass(isOutside?.(place))}
        >
          {place.tier === 'enterprise' ? t('settings.users.wholeAccount') : place.name}
          <Icon name="x" size="xs" aria-hidden="true" />
          <span className="sr-only">{t('settings.users.removeScope')}</span>
        </button>
      ))}

      <ForgeButton size="sm" disabled={pending} onClick={() => setPicking(true)}>
        {places.length ? t('settings.users.addAnotherPlace') : t('settings.users.addPlace')}
      </ForgeButton>

      {picking ? (
        <HierarchyPicker
          open
          onClose={() => setPicking(false)}
          title={title}
          root={root}
          accountUnavailable={accountUnavailable}
          value={places}
          onChange={onChange}
          note={note}
        />
      ) : null}
    </div>
  )
}

/**
 * One permission's scopes as chips, plus the same picker, restricted.
 *
 * `allowTiers` is what makes this different from the control above: a permission
 * declares the levels it may attach at, and the picker disables the rest rather
 * than hiding them — you still have to walk through a region to reach a plant.
 */
function ScopePicker({
  title,
  permission,
  scopes,
  nodeNames,
  root,
  accountUnavailable,
  pending = false,
  isOutside = null,
  note = null,
  onChange,
}) {
  const { t } = useTranslation(['app', 'common'])
  const [picking, setPicking] = useState(false)

  const allowed = useMemo(() => allowedTiers(permission), [permission])

  /* The chips, and what the picker opens holding. A scope whose node has not
     been read back yet shows its id — see `nodeNames`. */
  const chosen = useMemo(
    () =>
      [...scopes.values()].map(
        (scope) =>
          nodeNames.get(scope.id) ?? { ...scope, name: scope.id, code: null, unresolved: true },
      ),
    [scopes, nodeNames],
  )

  /* Rooted at a plant, the account level is not on offer whatever the
     permission allows, so naming it would point at a row that is not there. */
  const tierNames = [...allowed]
    .filter((tier) => !(root?.tier === 'site' && tier === 'enterprise'))
    .map((tier) => t(`settings.hierarchy.tiers.${tier}`, { defaultValue: tier }))
    .join(', ')

  return (
    <div className="mt-2 flex flex-wrap items-center gap-1.5">
      {chosen.length === 0 ? (
        /* Amber rather than silent: this row cannot be saved, and the server's
           reason for it would arrive as `scopes` on a 422 with nothing pointing
           at which row. */
        <span className="text-micro text-on-amber">{t('settings.users.scopeRequired')}</span>
      ) : (
        chosen.map((node) => (
          <button
            key={node.id}
            type="button"
            onClick={() => onChange(chosen.filter((entry) => entry.id !== node.id))}
            className={chipClass(isOutside?.(node))}
          >
            {node.tier === 'enterprise' ? t('settings.users.wholeAccount') : nodeLabel(node)}
            <Icon name="x" size="xs" aria-hidden="true" />
            <span className="sr-only">{t('settings.users.removeScope')}</span>
          </button>
        ))
      )}

      <ForgeButton size="sm" disabled={pending} onClick={() => setPicking(true)}>
        {t('settings.users.addScope')}
      </ForgeButton>

      {picking ? (
        <HierarchyPicker
          open
          onClose={() => setPicking(false)}
          title={title}
          root={root}
          accountUnavailable={accountUnavailable && allowed.has('enterprise')}
          value={chosen}
          onChange={onChange}
          allowTiers={allowed}
          note={[note, t('settings.users.tierRestricted', { tiers: tierNames })]
            .filter(Boolean)
            .join(' ')}
        />
      ) : null}
    </div>
  )
}

/**
 * The per-permission editor — the original control, now behind a choice.
 *
 * Unchanged in what it does: one row per permission, its own scopes, and the
 * picker filtered to the levels that permission allows. What changed is that
 * nobody is made to use it for a grant that is the same everywhere.
 */
function DetailedScopes({
  permissions,
  permissionById,
  root,
  accountUnavailable,
  pending,
  isOutside,
  note,
  nodeNames,
  onSetScopes,
}) {
  const { t } = useTranslation(['app', 'common'])

  return (
    <ul className="m-0 list-none p-0">
      {[...permissions].map(([permissionId, scopes]) => {
        const permission = permissionById.get(permissionId)

        return (
          <li key={permissionId} className="border-t border-subtle px-3.5 py-2.5 first:border-t-0">
            <div className="min-w-0">
              {/* A permission the catalogue does not describe. The backend seeds
                  the catalogue and ships on its own branch, so it will have a new
                  one first — rendering its id beats rendering an empty row. */}
              <p className="m-0 text-body-sm text-ink">{permission?.name ?? permissionId}</p>
              {permission?.description ? (
                <p className="m-0 text-micro text-ink-mute">{permission.description}</p>
              ) : null}
            </div>

            {permission ? (
              <ScopePicker
                title={t('settings.users.chooseScopeFor', { permission: permission.name })}
                permission={permission}
                scopes={scopes}
                nodeNames={nodeNames}
                root={root}
                accountUnavailable={accountUnavailable}
                pending={pending}
                isOutside={isOutside}
                note={note}
                onChange={(chosen) => onSetScopes(permissionId, chosen)}
              />
            ) : (
              /* No catalogue entry means no `targetLevels`, so there is nothing
                 to restrict a picker to and no honest way to offer one. The
                 scopes already on the grant are shown and kept. */
              <p className="mt-2 mb-0 text-micro text-ink-mute">
                {t('settings.users.permissionUnknown')}
              </p>
            )}
          </li>
        )
      })}
    </ul>
  )
}

/**
 * Today as `YYYY-MM-DD`, in the reader's own timezone.
 *
 * Not `toISOString().slice(0, 10)`, which is UTC and is a day out for anybody
 * west of Greenwich through their evening — an assignment starting "today" would
 * be dated tomorrow for somebody in Findlay at 8pm. `en-CA` is the shortest
 * spelling of the ISO order that `Intl` will produce.
 */
const today = () => new Intl.DateTimeFormat('en-CA').format(new Date())

/** A node, named with its code when it has one — the machine identifier reads as one. */
const nodeLabel = (node) => (node.code ? `${node.name} · ${node.code}` : node.name)

/**
 * Whether a scope sits outside the plant that owns its role.
 *
 * The account is always outside, and so is any other plant. A line or asset is
 * taken as inside: the pickers are rooted at the plant so a new one cannot come
 * from elsewhere, and a stored one already passed the server's ancestry check.
 * Proving it here would mean an ancestors read per chip for a case that cannot
 * arise from this form.
 */
const outsidePlant = (scope, plantId) =>
  scope.tier === 'enterprise' || (scope.tier === 'site' && scope.id !== plantId)

/** A place chip; red when it breaks its role's plant rule. */
const chipClass = (invalid) =>
  cn(
    'inline-flex items-center gap-1.5 rounded-pill border px-2.5 py-[3px] text-micro transition-colors hover:bg-sunken',
    invalid ? 'border-red tint-red text-on-red' : 'border-hairline text-ink-dim',
  )

/** How many node records the name resolver reads at once — see `nodeNames`. */
const NAME_BATCH = 6

/**
 * `person.roles` → the Map of Maps the form edits.
 *
 * Reconciled against the roles as they stand now by `reconcileGrants`, which is
 * shared with the row menu's Deactivate — the same PUT, the same rule, and the
 * one place it is explained. A permission the role has gained arrives here with
 * an empty scope Map, which the card then shows as needing a place.
 */
function initialGrants(person, roleById) {
  return new Map(
    reconcileGrants(person?.roles, roleById).map((grant) => [
      grant.roleId,
      new Map(
        grant.permissions.map((permission) => [
          permission.permissionId,
          new Map(permission.scopes.map((scope) => [scope.id, scope])),
        ]),
      ),
    ]),
  )
}
